import React, { useState, useEffect } from 'react';

import FilterComponent from '../../../SearchPage/FilterComponent';
import SearchFiltersPrimary from '../../../SearchPage/SearchFiltersPrimary/SearchFiltersPrimary';
import MainPanelHeader from '../../../SearchPage/MainPanelHeader/MainPanelHeader';
import TopbarSearchForm from '../../../../components/Topbar/TopbarSearchForm/TopbarSearchForm';

import {
  initialValues,
  searchParamsPicker,
  validFilterParams,
  groupListingFieldConfigs,
  validUrlQueryParamsFromProps,
  cleanSearchFromConflictingParams,
} from '../../../SearchPage/SearchPage.shared';

import {
  isMainSearchTypeKeywords,
  isOriginInUse,
} from '../../../../util/search';
import { createResourceLocatorString } from '../../../../util/routes';
import { parse } from '../../../../util/urlHelpers';

import css from '../../ProfilePage.module.css';

const FILTER_DROPDOWN_OFFSET = -14;

const SearchBar = ({
  intl,
  searchParams,
  location,
  config,
  history,
  routeConfiguration,
  userId,
}) => {
  const [currentQueryParams, setCurrentQueryParams] = useState({});

  useEffect(() => {
    if (location && config) {
      const queryParams = validUrlQueryParamsFromProps({ location, config });
      setCurrentQueryParams(queryParams);
    }
  }, []);

  const { keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { listingFields: listingFieldsConfig, masterCategoryFields } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
  const marketplaceCurrency = config.currency;
  const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);

  if (!listingFieldsConfig.find(x => masterCategoryFields.some(y => y.key === x.key))) {
    listingFieldsConfig.push(masterCategoryFields[0]);
  }

  const { searchParamsInURL } = searchParamsPicker(
    location.search,
    searchParams,
    listingFieldsConfig,
    defaultFiltersConfig,
    sortConfig,
    isOriginInUse(config)
  );

  const validQueryParams = validFilterParams(
    searchParamsInURL,
    listingFieldsConfig,
    defaultFiltersConfig,
    false
  );

  const isKeywordSearch = isMainSearchTypeKeywords(config);
  const showAsPopup = true;
  const idPrefix = "SearchFiltersPrimary";

  const defaultFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => f.key !== 'keywords')
      : defaultFiltersConfig;

  const [customPrimaryFilters] = groupListingFieldConfigs(
    listingFieldsConfig,
    activeListingTypes
  );

  const availablePrimaryFilters = [...customPrimaryFilters, ...defaultFilters];

  const getHandleChangedValueFn = (useHistoryPush) => {
    const { listingFields: listingFieldsConfig, masterCategoryFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    if (!listingFieldsConfig.find(x => masterCategoryFields.some(y => y.key === x.key))) {
      listingFieldsConfig.push(masterCategoryFields[0]);
    }

    const urlQueryParams = validUrlQueryParamsFromProps({ location, config });

    return updatedURLParams => {
      let result = {};

      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};

        result = {
          ...mergedQueryParams,
          ...updatedURLParams,
          ...keywordsMaybe,
          address,
          bounds,
        }

        return result;
      };

      setCurrentQueryParams(updater);

      if (useHistoryPush) {

        const search = cleanSearchFromConflictingParams(
          result,
          listingFieldsConfig,
          defaultFiltersConfig,
          sortConfig
        );

        history.push(
          createResourceLocatorString('ProfilePage', routeConfiguration, { id: userId }, search)
        );
      }
    };
  }

  const handleSubmit = (values) => {
    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };

    const searchParams = {
      ...currentQueryParams,
      ...topbarSearchParams(),
    };

    history.push(
      createResourceLocatorString('ProfilePage', routeConfiguration, { id: userId }, searchParams)
    );
  }

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };

  const initialSearchFormValues = topbarSearcInitialValues();

  return (
    <div className={css.searchBarWrapper}>
      <div className={css.formWrapper}>
        <TopbarSearchForm
          className={css.searchLink}
          desktopInputRoot={css.desktopInputRoot}
          onSubmit={handleSubmit}
          initialValues={initialSearchFormValues}
          appConfig={config}
        />
      </div>
      <div>
        <MainPanelHeader
          className={css.mainPanelMapVariant}
          hideResult={true}
          isSortByActive={false}
        >
          <SearchFiltersPrimary toggleSecondaryFiltersOpen={false}>
            {availablePrimaryFilters.map(c => {
              return (
                <FilterComponent
                  key={`SearchFiltersPrimary.${c.key}`}
                  idPrefix={idPrefix}
                  config={c}
                  marketplaceCurrency={marketplaceCurrency}
                  urlQueryParams={validQueryParams}
                  initialValues={initialValues({ location, config }, currentQueryParams)}
                  getHandleChangedValueFn={getHandleChangedValueFn}
                  intl={intl}
                  showAsPopup={showAsPopup}
                  contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                />
              );
            })}
          </SearchFiltersPrimary>
        </MainPanelHeader>
      </div>
    </div>
  )
}

export default SearchBar;
